/*
 *         ==== IMPORTANT NOTE ====
 *
 *         Use "bits" module to get
 *        access to following helpers
 *
 *         ==== IMPORTANT NOTE ====
 */

import { has, get, last, toLower, isString } from 'lodash-es'

import hex2rgb from 'hex-rgb'
import rgb2hex from '../../misc/rgb-hex.build'

const INIT_TTL = 5

const LIKE_HEX = /([0-9a-f]{6}|[0-9a-f]{3})/i
const LIKE_RGB = /(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*)/

const STRICT_HEX = /^#([0-9a-f]{6})$/i
const STRICT_RGB = /^(\d+,\d+,\d+)$/

const WORD_LIKE = /[a-z]{3,}/i

function isHexLike(raw) {
  return LIKE_HEX.test(raw)
}

export function isHex(raw) {
  return STRICT_HEX.test(raw)
}

function isRgbLike(raw) {
  return LIKE_RGB.test(raw)
}

export function isRgb(raw) {
  if (!STRICT_RGB.test(raw)) {
    return false
  }

  const bits = raw.split(',')
  for (const str of bits) {
    const num = parseInt(str)
    if (num < 0 || num > 255) {
      return false
    }
  }

  return true
}

function isWordLike(raw) {
  return true
    && !isRgbLike(raw)
    && !isHexLike(raw)
    && WORD_LIKE.test(raw)
}

export function toRgb(raw) {
  const ttl = safeGet(this, 'ttl', INIT_TTL) - 1
  if (ttl <= 0) {
    return raw
  }

  if (!isString(raw)) {
    return raw
  }

  const someColor = clearColor(raw)
  if (isRgb(someColor)) {
    return someColor
  }

  if (isWordLike(someColor)) {
    const cssColor = getCssColor(someColor)
    return toRgb.call({ ttl }, cssColor)
  }

  if (isRgbLike(someColor)) {
    const rgbColor = clearColor(last(LIKE_RGB.exec(someColor)))
    const isCorrect = isRgb(rgbColor)
    return isCorrect ? rgbColor : raw
  }

  if (isHexLike(someColor)) {
    const hexColor = toHex.call({ ttl }, someColor)
    const rgbColor = clearColor(hexToSimplifiedRgb(hexColor).join(','))
    const isCorrect = isRgb(rgbColor)
    return isCorrect ? rgbColor : raw
  }

  return raw
}

export function toHex(raw) {
  const ttl = safeGet(this, 'ttl', INIT_TTL) - 1
  if (ttl <= 0) {
    return raw
  }

  if (!isString(raw)) {
    return raw
  }

  const someColor = clearColor(raw)
  if (isHex(someColor)) {
    return someColor
  }

  if (isWordLike(someColor)) {
    const cssColor = getCssColor(someColor)
    return toHex.call({ ttl }, cssColor)
  }

  if (isRgbLike(someColor)) {
    const rgbColor = toRgb.call({ ttl }, someColor)
    const hexColor = clearColor('#' + rgb2hex(rgbColor))
    const isCorrect = isHex(hexColor)
    return isCorrect ? hexColor : raw
  }

  if (isHexLike(someColor)) {
    const match = last(LIKE_HEX.exec(someColor))
    const chars = match.length === 3
      ? match.replace(/(.)(.)(.)/, '$1$1$2$2$3$3')
      : match
    const hexColor = clearColor('#' + chars)
    const isCorrect = isHex(hexColor)
    return isCorrect ? hexColor : raw
  }

  return raw
}

function hexToSimplifiedRgb(hexColor) {
  return hex2rgb(hexColor, { format: 'array' }).slice(0, 3)
}

function clearColor(raw) {
  return toLower(raw.replace(/\s/g, ''))
}

function getCssColor(raw) {
  const someColor = clearColor(raw)

  const div = document.createElement('div')
  div.style.setProperty('color', someColor, 'important')
  document.body.appendChild(div)
  const cssColor = window.getComputedStyle(div).color
  document.body.removeChild(div)

  const isBlackOutput = cssColor === 'rgb(0, 0, 0)'
  const isBlackInput = false
    || someColor === 'black'
    || someColor === '#000'
    || someColor === '#000000'
    || someColor === 'rgb(0, 0, 0)'

  const isCorrect = !isBlackOutput || (isBlackInput && isBlackOutput)
  return isCorrect ? cssColor : raw
}

function safeGet(v, key, def) {
  if (has(v, key)) {
    return get(v, key, def)
  } else if (arguments.length === 3) {
    return def
  } else {
    return undefined
  }
}
